/**
 * Geofencing Tab Component
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MapPin, CheckCircle } from "lucide-react";

const METERS_PER_FOOT = 0.3048;
const METERS_PER_MILE = 1609.34;

const metersToUnit = (meters, unit) => {
  const m = meters || 0;
  return unit === "miles" ? m / METERS_PER_MILE : m / METERS_PER_FOOT;
};

// Keep real precision (don't round to a whole meter) so round-tripping a typed
// feet/miles value back through meters doesn't silently snap it to a nearby value.
const unitToMeters = (value, unit) => {
  const v = parseFloat(value);
  if (isNaN(v) || v < 0) return null;
  const meters = unit === "miles" ? v * METERS_PER_MILE : v * METERS_PER_FOOT;
  return Math.round(meters * 10000) / 10000;
};

export const GeofencingTab = ({ 
  settings, 
  setSettings, 
  onGetCurrentLocation, 
  onGeocodeAddress,
  geocodingLoading,
  adminNames
}) => {
  const currentUnit = settings.geofenceRadiusUnit || "feet";
  const [radiusText, setRadiusText] = useState(() =>
    String(Math.round(metersToUnit(settings.geofenceRadiusMeters, currentUnit) * 100) / 100)
  );

  // Re-derive the displayed text only when the UNIT toggles (Feet <-> Miles) —
  // never while the user is actively typing, so their keystrokes aren't fought.
  useEffect(() => {
    setRadiusText(String(Math.round(metersToUnit(settings.geofenceRadiusMeters, currentUnit) * 100) / 100));
  }, [currentUnit]);

  const handleRadiusChange = (e) => {
    const raw = e.target.value;
    setRadiusText(raw);
    const meters = unitToMeters(raw, currentUnit);
    if (meters !== null) {
      setSettings({ ...settings, geofenceRadiusMeters: meters });
    }
  };

  const handleRadiusBlur = () => {
    // If left empty/invalid, snap the display back to the last valid saved value.
    if (unitToMeters(radiusText, currentUnit) === null) {
      setRadiusText(String(Math.round(metersToUnit(settings.geofenceRadiusMeters, currentUnit) * 100) / 100));
    }
  };

  return (
    <Card className="border-amber-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <MapPin className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-xl text-amber-800">Geofencing</CardTitle>
              <CardDescription>Restrict sign-ins to a geographic area around your facility</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-amber-700">Enabled</Label>
            <Switch
              checked={settings.geofencingEnabled}
              onCheckedChange={(checked) => setSettings({...settings, geofencingEnabled: checked})}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {settings.geofencingEnabled && (
          <>
            <div>
              <Label className="text-amber-800">Office Address</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={settings.officeAddress}
                  onChange={(e) => setSettings({...settings, officeAddress: e.target.value})}
                  placeholder="Enter your facility address"
                  className="border-amber-200"
                />
                <Button 
                  onClick={onGeocodeAddress} 
                  variant="outline" 
                  className="border-amber-300"
                  disabled={geocodingLoading}
                >
                  {geocodingLoading ? "..." : "Lookup"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-800">Latitude</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={settings.officeLatitude || ""}
                  onChange={(e) => setSettings({...settings, officeLatitude: parseFloat(e.target.value) || null})}
                  placeholder="e.g., 40.7128"
                  className="border-amber-200"
                />
              </div>
              <div>
                <Label className="text-amber-800">Longitude</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={settings.officeLongitude || ""}
                  onChange={(e) => setSettings({...settings, officeLongitude: parseFloat(e.target.value) || null})}
                  placeholder="e.g., -74.0060"
                  className="border-amber-200"
                />
              </div>
            </div>

            <div>
              <Label className="text-amber-800">Radius</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  value={radiusText}
                  onChange={handleRadiusChange}
                  onBlur={handleRadiusBlur}
                  placeholder="1640"
                  className="border-amber-200 w-32"
                  data-testid="geofence-radius-input"
                />
                <Select
                  value={currentUnit}
                  onValueChange={(unit) => setSettings({ ...settings, geofenceRadiusUnit: unit })}
                >
                  <SelectTrigger className="w-28 border-amber-200" data-testid="geofence-radius-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feet">Feet</SelectItem>
                    <SelectItem value="miles">Miles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Sign-ins will only be allowed within this distance from your facility.
              </p>
            </div>

            <Button onClick={onGetCurrentLocation} variant="outline" className="border-amber-300">
              <MapPin className="w-4 h-4 mr-1" /> Use Current Location
            </Button>

            {settings.officeLatitude && settings.officeLongitude && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="w-5 h-5" />
                <span>Location set: {settings.officeLatitude.toFixed(4)}, {settings.officeLongitude.toFixed(4)}</span>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-amber-100">
              <div>
                <Label className="text-amber-800">Administrators bypass geofencing</Label>
                <p className="text-xs text-gray-500 mt-0.5" data-testid="geofence-admin-names">
                  {adminNames && adminNames.length > 0 ? adminNames.join(", ") : "No one currently holds this role"}
                </p>
              </div>
              <Switch
                checked={settings.geofenceExemptAdmins !== false}
                onCheckedChange={(checked) => setSettings({ ...settings, geofenceExemptAdmins: checked })}
                data-testid="geofence-exempt-admins-switch"
              />
            </div>
          </>
        )}
        
        {!settings.geofencingEnabled && (
          <p className="text-gray-500 text-sm">
            Enable geofencing to restrict sign-in operations to a specific geographic area around your facility.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default GeofencingTab;
